import { useState, useEffect } from "react";
import { useAuthenticatedFetchWithWorkspace } from "../Keycloak/useAuthenticatedFetchWithWorkspace";

export interface StoreUrlType {
    id: string | number;
    name: string;
    code?: string;
}

interface UrlTypeApiResponse {
    URL_TYPE_ID: string | number;
    URL_TYPE_NAME: string;
}

const useGetUrlTypes = () => {
    const [data, setData] = useState<StoreUrlType[] | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const { authFetch, isReady, isLoading: workspaceLoading, workspaceId } = useAuthenticatedFetchWithWorkspace();

    const fetchUrlTypes = async (): Promise<void> => {
        try {
            setLoading(true);
            setError("");
            const result: UrlTypeApiResponse[] = await authFetch(`/getUrlTypes`, { method: "GET" });
            if (!result) throw new Error("Failed to retrieve URL types.");

            const transformedData: StoreUrlType[] = result.map((urlType) => ({
                id: urlType.URL_TYPE_ID,
                name: urlType.URL_TYPE_NAME,
                code: urlType.URL_TYPE_ID?.toString(),
            }));

            setData(transformedData);
        } catch (err: unknown) {
            if (err instanceof Error) {
                setError(err.message);
            }
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (workspaceLoading) {
            setLoading(true);
            return;
        }

        if (!isReady) {
            setLoading(false);
            return;
        }

        setData(null);
        setLoading(true);
        setError("");
        fetchUrlTypes();
    }, [isReady, workspaceLoading, workspaceId]);

    return { data, loading, error };
};

export default useGetUrlTypes;
